import { useCallback, useRef, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import type { Instance } from '@/types/instance';
import type { Endpoint } from '@/types/endpoint';

interface ResourceCreateDialogProps {
  resourceType: 'volume' | 'network';
  instances: Instance[];
  endpoints: Endpoint[];
  onSubmit: (data: { name: string; instance_id: number; endpoint_id: number; driver?: string }) => Promise<void>;
  open: boolean;
  onClose: () => void;
}

const DRIVERS: Record<'volume' | 'network', string[]> = {
  volume: ['local', 'nfs', 'tmpfs'],
  network: ['bridge', 'overlay', 'host'],
};

const selectClass =
  'h-8 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive dark:bg-input/30';

/**
 * The form is mounted only while open, so every open starts from a blank form (no reset effect).
 * Instances and endpoints are supplied by the caller (the pages load them with un-paged lists today).
 */
export function ResourceCreateDialog(props: ResourceCreateDialogProps) {
  const submittingRef = useRef(false);
  const { open, onClose } = props;
  const { captureOpener, restoreFocus } = useDialogFocusRestore(); // lives in the shell: outlives CreateForm
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submittingRef.current) onClose();
      }}
    >
      {open ? <CreateForm {...props} submittingRef={submittingRef} captureOpener={captureOpener} restoreFocus={restoreFocus} /> : null}
    </Dialog>
  );
}

function CreateForm({
  resourceType,
  instances,
  endpoints,
  onSubmit,
  onClose,
  submittingRef,
  captureOpener,
  restoreFocus,
}: ResourceCreateDialogProps & {
  submittingRef: { current: boolean };
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}) {
  const nameRef = useRef<HTMLInputElement>(null);
  const [errorSeq, setErrorSeq] = useState(0);
  // Fires when the alert mounts (key changes per failure), after React has committed it.
  const focusAlert = useCallback((el: HTMLParagraphElement | null) => { el?.focus(); }, []);
  const [name, setName] = useState('');
  const [instanceId, setInstanceId] = useState<number | undefined>();
  const [endpointId, setEndpointId] = useState<number | undefined>();
  const [driver, setDriver] = useState(DRIVERS[resourceType][0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);

  const label = resourceType === 'volume' ? 'volume' : 'network';
  const filteredEndpoints = endpoints.filter((ep) => (instanceId ? ep.instance_id === instanceId : true));

  const nameInvalid = attempted && !name;
  const instanceInvalid = attempted && !instanceId;
  const endpointInvalid = attempted && !endpointId;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setAttempted(true);
    if (!name || !instanceId || !endpointId) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ name, instance_id: instanceId, endpoint_id: endpointId, driver });
      submittingRef.current = false;
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : `Failed to create ${label}`);
      setErrorSeq((n) => n + 1);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const block = (e: Event) => {
    if (submittingRef.current) e.preventDefault();
  };

  return (
    <DialogContent
      className="sm:max-w-md"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        nameRef.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={block}
      onPointerDownOutside={block}
      onInteractOutside={block}
    >
      <DialogHeader>
        <DialogTitle>Create {label}</DialogTitle>
        <DialogDescription>
          Choose where the {label} is created. All fields are required.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void handleSubmit(e)} noValidate className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="resource-name">Name</Label>
          <Input
            ref={nameRef}
            id="resource-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="my-resource"
            required
            aria-required="true"
            aria-invalid={nameInvalid}
            aria-describedby={nameInvalid ? 'resource-name-error' : undefined}
            readOnly={submitting}
          />
          {nameInvalid ? (
            <p id="resource-name-error" className="text-sm text-destructive">
              Name is required.
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="resource-instance">Instance</Label>
          <select
            id="resource-instance"
            className={selectClass}
            value={instanceId ?? ''}
            onChange={(e) => {
              if (submitting) return;
              setInstanceId(e.target.value ? parseInt(e.target.value, 10) : undefined);
              setEndpointId(undefined);
            }}
            required
            aria-required="true"
            aria-invalid={instanceInvalid}
            aria-describedby={instanceInvalid ? 'resource-instance-error' : undefined}
            aria-disabled={submitting}
          >
            <option value="">Select instance</option>
            {instances.map((inst) => (
              <option key={inst.id} value={inst.id}>
                {inst.name}
              </option>
            ))}
          </select>
          {instanceInvalid ? (
            <p id="resource-instance-error" className="text-sm text-destructive">
              Select an instance.
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="resource-endpoint">Endpoint</Label>
          <select
            id="resource-endpoint"
            className={selectClass}
            value={endpointId ?? ''}
            onChange={(e) => {
              if (!submitting) setEndpointId(e.target.value ? parseInt(e.target.value, 10) : undefined);
            }}
            required
            aria-required="true"
            aria-invalid={endpointInvalid}
            aria-describedby={endpointInvalid ? 'resource-endpoint-error' : 'resource-endpoint-help'}
            disabled={!instanceId}
            aria-disabled={submitting}
          >
            <option value="">Select endpoint</option>
            {filteredEndpoints.map((ep) => (
              <option key={ep.id} value={ep.id}>
                {ep.name}
              </option>
            ))}
          </select>
          {endpointInvalid ? (
            <p id="resource-endpoint-error" className="text-sm text-destructive">
              Select an endpoint.
            </p>
          ) : (
            <p id="resource-endpoint-help" className="text-sm text-muted-foreground">
              {instanceId ? 'Endpoints on the selected instance.' : 'Select an instance first.'}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="resource-driver">Driver</Label>
          <select
            id="resource-driver"
            className={selectClass}
            value={driver}
            onChange={(e) => {
              if (!submitting) setDriver(e.target.value);
            }}
            aria-disabled={submitting}
          >
            {DRIVERS[resourceType].map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        {error ? (
          <p key={errorSeq} ref={focusAlert} tabIndex={-1} role="alert" className="text-sm text-destructive outline-none">
            {error}
          </p>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" aria-disabled={submitting} onClick={() => { if (!submitting) onClose(); }}>
            Cancel
          </Button>
          <Button type="submit" aria-disabled={submitting} aria-busy={submitting} className={submitting ? 'pointer-events-none opacity-70' : undefined}>
            {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
